import React, { useEffect, useRef } from 'react';
import {
  AppOutline,
  UserOutline,
  UnorderedListOutline,
} from 'antd-mobile-icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { selectUser, selectIsAuthenticated } from '../../store/slices/authSlice';
import {
  fetchUserScooters,
  selectCurrentScooter,
  selectScooterLoading,
  selectScooters,
  setSelectedScooter,
} from '../../store/slices/scooterSlice';
import MobileUserProfile from '../molecules/MobileUserProfile';
import ScooterSelector from '../organisms/ScooterSelector';
import { riderLight } from '../../theme/riderTheme';
import {
  RiderShell,
  ScreenScroll,
  AppBar,
  AppBarLeft,
  AppBarCenter,
  AppBarRight,
  AppBarEyebrow,
  AppBarTitle,
  RiderTabBar,
  RiderTabItem,
} from '../../theme/surfacePrimitives';

// Route → display title. Pages that render their own large PageHeader leave
// this blank so the bar stays minimal (just the scooter chip + avatar) and
// avoids duplicating the page identity. Routes without a page-level header
// (e.g. the auth callbacks) keep their title in the bar.
const PATH_TITLES: Record<string, string> = {
  '/dashboard': '',
  '/packs': '',
  '/packs/browse': '',
  '/scooter-packs': 'Scooter Packs',
  '/wallet': '',
  '/wallet/khalti/return': '',
  '/account': '',
};

const SCOOTER_SCOPED_PATH_PREFIXES = ['/dashboard', '/packs', '/scooter-packs'];

interface MobileLayoutProps {
  children?: React.ReactNode;
  title?: string;
  showTabBar?: boolean;
}

// Devanagari rupee glyph (रु) — falls back to a Devanagari-capable system
// font on machines whose UI font doesn't cover the script. The tab bar
// container takes care of sizing (22px) so this only needs to declare
// the font stack and weight. Matches the wallet card eyebrow marker.
const WalletGlyph = styled.span`
  font-family: 'Geist', 'Noto Sans Devanagari', system-ui, sans-serif;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const tabs = [
  { key: '/dashboard', title: 'Home', icon: <AppOutline /> },
  { key: '/packs', title: 'Packs', icon: <UnorderedListOutline /> },
  {
    key: '/wallet',
    title: 'Wallet',
    icon: (
      <WalletGlyph aria-hidden="true">रु</WalletGlyph>
    ),
  },
  { key: '/account', title: 'Account', icon: <UserOutline /> },
];

const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  title,
  showTabBar = true,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const scooters = useAppSelector(selectScooters);
  const currentScooter = useAppSelector(selectCurrentScooter);
  const scooterLoading = useAppSelector(selectScooterLoading);
  const scooterInitAttemptedRef = useRef(false);

  const displayTitle = title ?? PATH_TITLES[location.pathname] ?? '';
  const isScooterScopedPage = SCOOTER_SCOPED_PATH_PREFIXES.some(
    path => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  const handleTabChange = (key: string) => {
    navigate(key);
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      !isScooterScopedPage ||
      currentScooter ||
      scooterLoading.scooters ||
      scooterLoading.switching
    ) {
      return;
    }

    if (scooterInitAttemptedRef.current && scooters.length === 0) {
      return;
    }

    let cancelled = false;

    const ensureScooterSelected = async () => {
      try {
        scooterInitAttemptedRef.current = true;
        const availableScooters =
          scooters.length > 0
            ? scooters
            : await dispatch(fetchUserScooters()).unwrap();

        if (!cancelled && availableScooters.length > 0) {
          dispatch(setSelectedScooter(availableScooters[0].vin));
        }
      } catch (error) {
        console.error('Failed to initialize scooter selection:', error);
      }
    };

    ensureScooterSelected();

    return () => {
      cancelled = true;
    };
  }, [
    currentScooter,
    dispatch,
    isAuthenticated,
    isScooterScopedPage,
    scooterLoading.scooters,
    scooterLoading.switching,
    scooters,
  ]);

  // Closest matching tab for activeKey (e.g. /packs/browse -> /packs)
  const activeTabKey =
    tabs.find(t => location.pathname.startsWith(t.key))?.key || '';

  return (
    <ThemeProvider theme={riderLight as never}>
      <RiderShell>
        <AppBar>
          <AppBarLeft>
            {isAuthenticated && isScooterScopedPage ? <ScooterSelector /> : null}
          </AppBarLeft>
          <AppBarCenter>
            {displayTitle && (
              <>
                <AppBarEyebrow>Rider</AppBarEyebrow>
                <AppBarTitle>{displayTitle}</AppBarTitle>
              </>
            )}
          </AppBarCenter>
          <AppBarRight>
            {isAuthenticated && user ? <MobileUserProfile /> : null}
          </AppBarRight>
        </AppBar>

        <ScreenScroll key={location.pathname}>
          {children || <Outlet />}
        </ScreenScroll>

        {showTabBar && (
          <RiderTabBar aria-label="Primary navigation">
            {tabs.map(tab => (
              <RiderTabItem
                key={tab.key}
                $active={tab.key === activeTabKey}
                onClick={() => handleTabChange(tab.key)}
                aria-current={tab.key === activeTabKey ? 'page' : undefined}
                aria-label={tab.title}
              >
                {tab.icon}
                <span>{tab.title}</span>
              </RiderTabItem>
            ))}
          </RiderTabBar>
        )}
      </RiderShell>
    </ThemeProvider>
  );
};

export default MobileLayout;
